import { useEffect, useRef } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
// WEB PUSH FOR BORROWERS IN A BROWSER — WHICH IS MOST OF THEM.
//
// 🚨 WHAT WAS WRONG. usePushNotifications.js opens `if (!isNative()) return`, so
// device registration only ever happened inside the Capacitor shell. A borrower
// on app.godoorly.com in a browser had no row in device_tokens, and every
// message a Doorly rep sent logged "[Push] No devices for user N — skipping"
// and delivered nothing. Jesse: "no one is responding to them." They were not
// being told. The email went; a notification did not.
//
// 🔑 FCM-FOR-WEB, NOT A SECOND PUSH STACK. A web registration token is an
// ordinary FCM token, so the backend's existing sendEachForMulticast call
// delivers to it beside iOS and Android — one send path, one template set, one
// stale-token prune, one badge rule. A VAPID/web-push implementation would have
// been a parallel system to keep in step, and the pruning and routing would
// have had to be written twice.
//
// ⚠️ PERMISSION IS NEVER REQUESTED ON LOAD. A browser that is asked for
// notification permission the instant a page opens is a browser the borrower
// denies — and a denial is close to permanent, needing a trip into site
// settings to undo. So this only acts when permission is ALREADY granted;
// asking is a deliberate act behind a button (requestWebPushPermission below),
// which a surface can call after explaining why.
//
// ⚠️ AND SAFARI ON iOS ONLY SUPPORTS THIS FOR AN INSTALLED PWA. Checked rather
// than assumed: Notification and serviceWorker both exist in iOS Safari, and
// getToken fails at the push-subscribe step instead — so the guard is on
// isSupported() from the SDK plus a try/catch, not on a user-agent string.
// ─────────────────────────────────────────────────────────────────────────────

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Running inside the Capacitor native shell? Then the native hook owns this and
// this one must stay out of the way — two registrations for one user is two
// notifications for one message.
const isNative = () =>
  typeof window !== 'undefined' && !!(window.Capacitor?.isNativePlatform?.());

/**
 * The Firebase web config, from build-time env.
 *
 * ⚠️ NOT SECRETS, AND STILL NOT HARDCODED. These ship in the bundle by design
 * (every Firebase web app's do), but keeping them in one place means the
 * service worker and the page cannot disagree about which project they are
 * talking to — which is a failure that looks exactly like "push is broken".
 */
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

/** Is web push even possible here, and is it configured? */
export function webPushConfigured() {
  return !!(firebaseConfig.apiKey && firebaseConfig.projectId
    && firebaseConfig.messagingSenderId && VAPID_KEY);
}

export function webPushAvailable() {
  return typeof window !== 'undefined'
    && 'Notification' in window
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && !isNative();
}

/** 'granted' | 'denied' | 'default' | 'unavailable' */
export function webPushPermission() {
  if (!webPushAvailable()) return 'unavailable';
  return Notification.permission;
}

// ⚠️ THE CONFIG RIDES IN THE REGISTRATION URL. public/firebase-messaging-sw.js
// is served to anybody who asks for it, so it reads its config from here rather
// than carrying a copy that a config change would leave stale in a cached
// worker.
const swUrl = () => {
  const q = new URLSearchParams({
    apiKey: firebaseConfig.apiKey || '',
    authDomain: firebaseConfig.authDomain || '',
    projectId: firebaseConfig.projectId || '',
    messagingSenderId: firebaseConfig.messagingSenderId || '',
    appId: firebaseConfig.appId || '',
  });
  return `/firebase-messaging-sw.js?${q.toString()}`;
};

async function registerToken(jwt) {
  const { initializeApp, getApps } = await import('firebase/app');
  const { getMessaging, getToken, isSupported } = await import('firebase/messaging');
  if (!(await isSupported())) {
    console.warn('[WebPush] this browser does not support FCM web messaging');
    return { ok: false, reason: 'unsupported' };
  }
  const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);
  const registration = await navigator.serviceWorker.register(swUrl());
  const messaging = getMessaging(app);
  const deviceToken = await getToken(messaging, {
    vapidKey: VAPID_KEY,
    serviceWorkerRegistration: registration,
  });
  if (!deviceToken) return { ok: false, reason: 'no-token' };

  // 🔑 THE SAME ENDPOINT THE NATIVE HOOK USES, with platform 'web'. The backend
  // stores all three identically; the column exists so a count can say which
  // surface, not so anything branches on it.
  const res = await fetch(`${API}/api/notifications/register-device`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ device_token: deviceToken, platform: 'web' }),
  });
  if (!res.ok) return { ok: false, reason: `register-${res.status}` };
  return { ok: true };
}

/**
 * 🚨 ASKING IS A DELIBERATE ACT. Call this from a button, after saying why —
 * a denial is close to permanent and costs the borrower a trip into site
 * settings to undo, so it must never be spent on a prompt they did not expect.
 *
 * @returns {Promise<{ok: boolean, reason?: string}>}
 */
export async function requestWebPushPermission(jwt) {
  if (!webPushAvailable()) return { ok: false, reason: 'unavailable' };
  if (!webPushConfigured()) return { ok: false, reason: 'not-configured' };
  if (!jwt) return { ok: false, reason: 'not-signed-in' };
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return { ok: false, reason: permission };
    return await registerToken(jwt);
  } catch (e) {
    // ⚠️ NEVER THROWS INTO A CLICK HANDLER. A rejected subscribe on iOS Safari
    // outside an installed PWA lands here, and it is a normal outcome rather
    // than an error the borrower can do anything about.
    console.warn('[WebPush] permission/registration failed:', e.message);
    return { ok: false, reason: 'failed' };
  }
}

/**
 * Register the browser for push IF permission is already granted.
 *
 * ⚠️ IT DOES NOT PROMPT. See the header. This is the part that runs on every
 * load so a granted borrower's token stays fresh — FCM rotates them, and a
 * stale token is pruned server-side the first time a send fails against it, at
 * which point this is the only thing that puts a working one back.
 *
 * @param {string|null} jwt  Doorly JWT (null = not logged in)
 */
export function useWebPushNotifications(jwt) {
  const done = useRef(false);

  useEffect(() => {
    if (!jwt || done.current) return;
    if (!webPushAvailable()) return;
    if (!webPushConfigured()) {
      // 🔑 SAID ONCE, AND SAID AS CONFIGURATION rather than failure. "Not set
      // up" and "broken" are the pair that cost three rounds on the
      // authorization card; they must not look the same in a console either.
      console.warn('[WebPush] REACT_APP_FIREBASE_* / VAPID key not set at build time — '
        + 'web push is not configured. Native push is unaffected.');
      done.current = true;
      return;
    }
    if (Notification.permission !== 'granted') return;

    done.current = true;
    registerToken(jwt)
      .then((r) => {
        if (r.ok) console.log('[WebPush] browser registered for notifications');
        else console.warn('[WebPush] not registered:', r.reason);
      })
      .catch((e) => console.warn('[WebPush] registration error:', e.message));
  }, [jwt]);
}
